"use client";

import { useActionState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useEditableForm } from "@/stores/useEditableFormStore";

export interface FormActionState {
  success: boolean;
  error?: string;
  fieldErrors?: Record<string, string>;
}

export interface UseFormActionStateOptions<TState extends FormActionState> {
  action: (
    state: Awaited<TState>,
    payload: FormData
  ) => TState | Promise<TState>;
  initialState: Awaited<TState>;
  successMessage?: string;
  errorMessage?: string;
  formId?: string | string[];
  onSuccess?: () => void;
  onError?: (error: string) => void;
  cancelEditingOnSuccess?: boolean;
  shouldFireSuccess?: (state: Awaited<TState>) => boolean;
}

export function useFormActionState<TState extends FormActionState>({
  action,
  initialState,
  successMessage = "Changes saved successfully",
  errorMessage,
  formId,
  onSuccess,
  onError,
  cancelEditingOnSuccess = true,
  shouldFireSuccess,
}: UseFormActionStateOptions<TState>) {
  const { cancelEditing } = useEditableForm();
  const [state, formAction, isPending] = useActionState<TState, FormData>(
    action,
    initialState
  );

  const formIdKey = useMemo(
    () => (Array.isArray(formId) ? formId.join(",") : formId),
    [formId?.toString()]
  );

  // Prevent duplicate toasts: only fire once per action result.
  // Resets when a new submission starts (isPending becomes true).
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (isPending) {
      hasHandledRef.current = false;
    }
  }, [isPending]);

  useEffect(() => {
    if (hasHandledRef.current || isPending) {
      return;
    }

    if (state.success) {
      if (shouldFireSuccess && !shouldFireSuccess(state)) {
        hasHandledRef.current = true;
        return;
      }
      hasHandledRef.current = true;
      toast.success(successMessage);

      if (cancelEditingOnSuccess) {
        if (Array.isArray(formId)) {
          formId.forEach((id) => cancelEditing(id));
        } else {
          cancelEditing(formId);
        }
      }

      onSuccess?.();
    } else if (state.fieldErrors && Object.keys(state.fieldErrors).length > 0) {
      hasHandledRef.current = true;
      const message =
        Object.values(state.fieldErrors)[0] ||
        errorMessage ||
        "Please fix the highlighted fields";
      toast.error(message);

      onError?.(message);
    } else if (state.error) {
      hasHandledRef.current = true;
      const message = errorMessage || state.error;
      toast.error(message);

      onError?.(state.error);
    }
  }, [
    state,
    isPending,
    successMessage,
    errorMessage,
    formIdKey,
    cancelEditingOnSuccess,
    cancelEditing,
    onSuccess,
    onError,
    shouldFireSuccess,
  ]);

  return {
    state,
    formAction,
    isPending,
  };
}
